import React, { useEffect, useState, useRef, useMemo } from "react";
import SidebarTechTree from "./SidebarTechTree";
import EliteMarkdownViewer from "./EliteMarkdownViewer";
import FloatingActions from "./FloatingActions";
import ThemeToggle from "./ThemeToggle";
import EliteSelectionModal from "./EliteSelectionModal";
import PremiumUpgradeModal from "./PremiumUpgradeModal";
import {
  BookOpen,
  ChevronDown,
  Search,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  Target,
  PanelLeftClose,
  PanelLeftOpen,
  Clock,
  List,
  ChevronRight
} from "lucide-react";
import ChatBot from "./SkillGenBot/ChatBot";
import { useTheme } from "../contexts/ThemeContext";
import { useSubscription } from "../hooks/useSubscription";
import { useTenant } from "../Context/TenantContext";
import { useNavigate } from "react-router-dom";
import TechnologyVotingForm from "./TechnologyVotingForm";
import { updateLearningConsistency } from "../utils/consistency";

import API_BASE_URL from "../Config/config";

function slug(str) {
  return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

const EliteTechnologyMastery = () => {
  const [technologies, setTechnologies] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lockedFile, setLockedFile] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [tempSelectedTech, setTempSelectedTech] = useState(null);
  const [mode, setMode] = useState(null); 
  const [showChat, setShowChat] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [currentHierarchy, setCurrentHierarchy] = useState({ technology: '', topic: '', subtopic: '' });
  const [viewMode, setViewMode] = useState('tech-overview'); // tech-overview, topic-detail, subtopic-detail, file-reader

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedSubtopic, setSelectedSubtopic] = useState(null);

  const { theme } = useTheme();
  const { isPaid, elite, isLoaded } = useSubscription();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const eliteTechs = [
    { 
      id: 'java-premium', 
      title: 'Java Premium', 
      description: 'Master enterprise-grade Java: Advanced Multithreading, JVM Internals, and Spring Framework.',
      image: 'https://cdn-icons-png.flaticon.com/512/226/226777.png',
      color: 'purple',
      gradient: 'from-[#5B21B6] to-[#7C3AED]'
    },
    { 
      id: 'python-premium', 
      title: 'Python Premium', 
      description: 'Expert Python tracks: Data Science, High-performance AI, and Distributed Systems.',
      image: 'https://cdn-icons-png.flaticon.com/512/5968/5968350.png',
      color: 'indigo',
      gradient: 'from-[#7C3AED] to-[#A855F7]'
    }
  ];

  // Check if this is a tenant student (college flow) - they should have access
  const isTenantStudent = tenant && tenant.domain !== 'theskillgenie.com';

  // Determine user access level
  const hasPremiumAccess = isPaid || isTenantStudent;
  const hasSelection = elite?.selection;

  // Final filtered list of technologies based on user's selection status
  const filteredTechnologies = technologies.filter(tech => {
    return true; // Requirement: All technologies should always be visible and accessible
  });

  const [flatSubtopics, setFlatSubtopics] = useState([]);
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');
  const [readingProgress, setReadingProgress] = useState(0);
  const [readingTime, setReadingTime] = useState(0);

  const chatBotRef = useRef(null);

  // Resume Learning Logic
  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId || !isLoaded) return;

    const resumeLearning = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/progress/dashboard?last_only=true`, {
          headers: { 'user-id': userId }
        });
        const data = await response.json();
        
        if (data.success && data.progress) {
          const prog = data.progress;
          // Set state to resume
          setSelectedFile(prog.file_path);
          setCurrentHierarchy({
            technology: prog.technology,
            topic: prog.topic,
            subtopic: prog.subtopic
          });
          
          // Restore scroll position after a short delay to allow content to load
          setTimeout(() => {
            const container = document.getElementById('mastery-content-area');
            if (container && prog.scroll_position) {
              const targetScroll = (prog.scroll_position / 100) * (container.scrollHeight - container.clientHeight);
              container.scrollTo({ top: targetScroll, behavior: 'smooth' });
            }
          }, 2000);
        }
      } catch (err) {
        console.error('Failed to resume learning:', err);
      }
    };

    resumeLearning();
  }, [isLoaded]);

  // Header for hierarchy should stay visible. 
  // We can use the same logic for sticky.
  
  // Implementation of Hierarchy Detection on Scroll
  useEffect(() => {
    const container = document.getElementById('mastery-content-area');
    if (!container || !selectedFile) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
           // If we find an ID that corresponds to a subtopic or heading, update.
           // For now, we rely on the TOC active heading but we can also update hierarchy here if multiple topics are in one file.
        }
      });
    }, { threshold: 0.5 });

    // Observe all headings
    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings, selectedFile]);
  useEffect(() => {
    const container = document.getElementById('mastery-content-area');
    if (!container) return;

    const handleScroll = () => {
      // Heading detection
      const headingElements = headings.map(h => document.getElementById(h.id)).filter(Boolean);
      let current = '';
      for (const el of headingElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top < 200) {
          current = el.id;
        } else {
          break;
        }
      }
      setActiveHeading(current);

      // Reading progress
      const totalHeight = container.scrollHeight - container.clientHeight;
      const progress = totalHeight > 0 ? (container.scrollTop / totalHeight) * 100 : 0;
      setReadingProgress(progress);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll position restoration
  useEffect(() => {
    if (selectedFile) {
      const saved = localStorage.getItem('last_read_doc');
      if (saved) {
        const data = JSON.parse(saved);
        if (data.path === selectedFile && data.scrollTop) {
           setTimeout(() => {
              const container = document.getElementById('mastery-content-area');
              if (container) {
                container.scrollTo({ top: data.scrollTop, behavior: 'instant' });
              }
           }, 100);
        }
      }
    }
  }, [selectedFile]);

  // Save scroll position
  useEffect(() => {
    const container = document.getElementById('mastery-content-area');
    if (!container) return;

    const handleScroll = () => {
       if (selectedFile) {
          const saved = JSON.parse(localStorage.getItem('last_read_doc') || "{}");
          if (saved.path === selectedFile) {
             saved.scrollTop = container.scrollTop;
             localStorage.setItem('last_read_doc', JSON.stringify(saved));
          }
       }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedFile]);

  // Save progress to backend periodically
  useEffect(() => {
    if (!selectedFile || !currentHierarchy) return;

    const saveProgress = async () => {
      const userId = localStorage.getItem('user_id') || getUserId();
      if (!userId) return;

      try {
        await fetch(`${API_BASE_URL}/api/progress/reading`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'user-id': userId
          },
          body: JSON.stringify({
            module: 'Elite Mastery',
            technology: currentHierarchy.technology,
            topic: currentHierarchy.topic,
            subtopic: currentHierarchy.subtopic,
            file_path: selectedFile,
            scroll_position: Math.round(readingProgress),
            reading_percentage: Math.round(readingProgress),
            completion_percentage: Math.round(readingProgress),
            time_spent: 30, // 30 seconds interval
            status: readingProgress > 90 ? 'Completed' : 'In Progress'
          })
        });
        
        // Also update consistency
        updateLearningConsistency(userId);
      } catch (err) {
        console.error('Failed to save reading progress:', err);
      }
    };

    const interval = setInterval(saveProgress, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [selectedFile, currentHierarchy, readingProgress]);

  // Flatten subtopics for navigation
  useEffect(() => {
    if (filteredTechnologies.length > 0) {
      const flattened = [];
      filteredTechnologies.forEach(tech => {
        tech.topics.forEach(topic => {
          topic.subtopics.forEach(sub => {
            flattened.push({
              ...sub,
              techName: tech.technology,
              topicName: topic.topic
            });
          });
        });
      });
      setFlatSubtopics(flattened);
    }
  }, [filteredTechnologies]);

  const currentIndex = flatSubtopics.findIndex(s => s.file === selectedFile);
  const handlePrev = () => {
    if (currentIndex > 0) {
      setSelectedFile(flatSubtopics[currentIndex - 1].file);
    }
  };

  const handleNext = () => {
    if (currentIndex < flatSubtopics.length - 1) {
      setSelectedFile(flatSubtopics[currentIndex + 1].file);
    }
  };


  // Get user ID
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.userId || user?.user_id || null;
    } catch {
      return null;
    }
  };

  // Load Elite Technologies mappings
  useEffect(() => {
    const loadTechMappings = async () => {
      const techs = [];
      const targets = hasSelection ? [hasSelection] : ['java-premium', 'python-premium'];
      
      for (const techId of targets) {
        try {
          const res = await fetch(`/docs/${techId}/mapping.json`);
          if (!res.ok) continue;
          const mappingData = await res.json();

          const topicsMap = {};
          Object.entries(mappingData).forEach(([key, path]) => {
            const parts = path.split('/');
            const topicName = parts[0].replace(/_/g, ' ');
            const subtopicTitle = parts[parts.length - 1].replace(/_/g, ' ').replace('.md', '');
            
            if (!topicsMap[topicName]) {
              topicsMap[topicName] = { topic: topicName, subtopics: [] };
            }
            topicsMap[topicName].subtopics.push({
              title: subtopicTitle,
              file: `/docs/${techId}/${path}`
            });
          });

          techs.push({
            technology: techId,
            topics: Object.values(topicsMap)
          });
        } catch (err) {
          console.error(`Failed to load mapping for ${techId}:`, err);
        }
      }
      setTechnologies(techs);
    };

    if (isLoaded) {
      loadTechMappings();
    }
  }, [isLoaded, hasSelection]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const techParam = params.get("tech");
    if (techParam && isLoaded) {
      setMode('learning');
      const saved = localStorage.getItem('last_read_doc');
      if (saved) {
          const data = JSON.parse(saved);
          if (data.tech === techParam && data.path) {
              setSelectedFile(data.path);
          }
      }
    }
  }, [isLoaded]);

  const handleTechClick = (techId) => {
    const isTenantStudent = tenant && tenant.domain !== 'theskillgenie.com';
    if (isTenantStudent) {
      setTempSelectedTech(techId);
      setMode('learning');
      return;
    }

    if (!hasSelection) {
      setTempSelectedTech(techId);
      setShowSelectionModal(true);
    } else if (hasSelection === techId) {
      setTempSelectedTech(techId);
      setMode('learning'); // Ensure learning mode is set
    }
  };

  const [showVotingForm, setShowVotingForm] = useState(true);

  // Check if user already submitted a suggestion in this session
  useEffect(() => {
    const hasSubmitted = localStorage.getItem('has_submitted_suggestion');
    if (hasSubmitted) {
      setShowVotingForm(false);
    }
  }, []);

  const handleSelectionSuccess = (selectedTech) => {
    setTempSelectedTech(selectedTech);
    setMode('learning');
    window.location.reload();
  };

  return (
    <div className={`${isDark ? "bg-[#0B0F1A]" : "bg-[#F8FAFC]"} min-h-screen flex flex-col font-sans selection:bg-primary/30`}>
      {/* 🧭 Top Navigation Bar */}
      <header className={`h-16 flex items-center justify-between px-6 sticky top-0 z-[70] border-b backdrop-blur-md ${
        isDark ? "bg-[#0B0F1A]/80 border-white/5" : "bg-white border-gray-200"
      }`}>
        <div className="flex items-center gap-4">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              
              if (selectedFile) {
                setSelectedFile(null);
                return;
              } 
              
              if (selectedSubtopic) {
                setSelectedSubtopic(null);
                return;
              } 
              
              if (selectedTopic) {
                setSelectedTopic(null);
                return;
              } 
              
              if (mode) {
                setMode(null);
                return;
              }

              // Determine the correct dashboard path based on domain/tenant
              const isMainPlatform = !tenant || 
                                   tenant.domain === 'theskillgenie.com' || 
                                   tenant.tenantKey === 'theskillgenie' ||
                                   window.location.hostname === 'theskillgenie.com' ||
                                   window.location.hostname === 'www.theskillgenie.com' ||
                                   window.location.hostname === 'localhost';
              
              const userType = localStorage.getItem('userType');
              let targetPath = '/dashboard';

              if (isMainPlatform) {
                if (userType === 'student') {
                  targetPath = '/dashboard';
                } else if (userType === 'college') {
                  targetPath = '/admin/dashboard';
                }
              } else {
                if (userType === 'student') {
                  targetPath = '/student/dashboard';
                } else if (userType === 'college') {
                  targetPath = '/admin/dashboard';
                }
              }

              // Use window.location.href to ensure a full navigation and clean state
              window.location.href = targetPath;
            }}
            type="button"
            className={`p-2 rounded-lg transition-colors ${
              isDark ? "hover:bg-white/10 text-white" : "hover:bg-gray-100 text-gray-700"
            }`}
            title="Go Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shadow-lg ${isDark ? "bg-blue-500 shadow-blue-500/20" : "bg-blue-600 shadow-blue-600/10"}`}>
              <Sparkles className="text-white" size={18} />
            </div>
            <span className={`text-lg font-bold tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
              Elite Technology Mastery
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <ThemeToggle />
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 flex overflow-hidden">
        {!isLoaded ? (
          <div className="flex-1 flex items-center justify-center">
             <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : !mode ? (
          /* 🏁 Grid View (Home) */
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-12">
                <h2 className={`text-3xl font-black uppercase tracking-tight ${isDark ? "text-white" : "text-gray-900"}`}>
                  {hasSelection ? "Your Selected Path" : "Choose Your Elite Path"}
                </h2>
                <p className={`mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSelection 
                    ? `Continue your journey in ${hasSelection.replace('-premium', '')} Mastery.` 
                    : "Select one technology to unlock elite documentation and practice tracks."}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
                {eliteTechs.map((tech) => {
                  const isLocked = hasSelection && hasSelection !== tech.id;
                  const isSelected = hasSelection === tech.id;
                  
                  return (
                    <div 
                      key={tech.id}
                      onClick={() => !isLocked && handleTechClick(tech.id)}
                      className={`group relative bg-white rounded-3xl p-8 border-2 transition-all duration-500 overflow-hidden ${
                        isLocked 
                        ? 'opacity-40 cursor-not-allowed border-gray-100 grayscale' 
                        : `cursor-pointer hover:border-primary hover:shadow-2xl hover:shadow-primary/10`
                      } ${isSelected ? 'border-primary shadow-xl shadow-primary/5 ring-4 ring-primary/10' : 'border-gray-100'}`}
                    >
                      <div className="flex flex-col h-full relative z-10">
                        <div className="flex justify-between items-start mb-6">
                          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center bg-gray-50 group-hover:scale-110 transition-transform duration-500`}>
                            <img src={tech.image} alt={tech.title} className="w-10 h-10 object-contain" />
                          </div>
                          {isLocked && <div className="text-2xl">🔒</div>}
                          {isSelected && (
                            <div className="bg-primary text-white text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-widest">
                              Active Path
                            </div>
                          )}
                        </div>

                        <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">
                          {tech.title}
                        </h3>
                        <p className="text-gray-500 text-sm leading-relaxed flex-1">
                          {tech.description}
                        </p>

                        <button className={`mt-8 w-full py-4 rounded-xl font-bold text-sm transition-all ${
                          isLocked 
                          ? 'bg-gray-100 text-gray-400' 
                          : 'bg-primary text-white group-hover:bg-primary-dark group-hover:shadow-lg group-hover:shadow-primary/20'
                        }`}>
                          {isLocked ? "Access Locked" : isSelected ? "Continue Practice" : "Start Learning"}
                        </button>
                      </div>
                      
                      {/* Decorative Background Elements */}
                      <div className={`absolute -bottom-4 -right-4 w-32 h-32 bg-primary/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-0`} />
                    </div>
                  );
                })}
              </div>

              {/* Voting Section */}
              {showVotingForm && (
                <div className="max-w-3xl mx-auto">
                  <div className="text-center mb-8">
                    <h3 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      Don't see your favorite tech?
                    </h3>
                    <p className={`mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      We're constantly expanding. Vote for what we should build next!
                    </p>
                  </div>
                  <TechnologyVotingForm isDark={isDark} onClose={() => setShowVotingForm(false)} />
                </div>
              )}
            </div>
          </div>
        ) : (
          /* 📖 Learning Mode (Docs) */
          <div className="flex-1 flex overflow-hidden relative w-full h-full">
            {/* Sidebar Overlay for Mobile */}
            {!isSidebarOpen && (
              <button 
                onClick={() => setIsSidebarOpen(true)}
                className={`fixed top-20 left-4 z-[60] p-2.5 rounded-xl shadow-2xl border transition-all hover:scale-110 active:scale-95 ${
                  isDark ? "bg-[#0B0F1A] border-white/10 text-[#5B21B6]" : "bg-white border-purple-100 text-[#5B21B6]"
                }`}
                title="Expand Sidebar"
              >
                <PanelLeftOpen size={22} />
              </button>
            )}

            {/* Left Sidebar */}
            <aside className={`
              fixed left-0 top-0 z-[100] h-screen w-80 flex-shrink-0 flex flex-col overflow-hidden
              transition-all duration-300 ease-in-out
              ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
              ${isDark ? "bg-[#0B0F1A]" : "bg-[#5B21B6] border-r-0"}
            `}>
              <div className={`p-4 flex items-center justify-between flex-shrink-0 border-b ${isDark ? "border-white/5" : "border-white/10"}`}>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setMode(null)}
                    className={`p-1.5 rounded-full transition-colors ${isDark ? "hover:bg-white/10 text-white" : "hover:bg-white/20 text-white"}`}
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div className="uppercase text-[10px] font-black tracking-[0.2em] text-white whitespace-nowrap">
                    Tech Documents
                  </div>
                </div>
                <button 
                  onClick={() => setIsSidebarOpen(false)}
                  className={`p-1.5 rounded-lg transition-all ${
                    isDark ? "hover:bg-white/10 text-gray-400" : "hover:bg-white/20 text-white"
                  }`}
                  title="Collapse Sidebar"
                >
                  <PanelLeftClose size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <SidebarTechTree
                  data={filteredTechnologies}
                  onSelectSubtopic={async (file, isLocked, tech, topic, subtopic) => {
                    if (isLocked) {
                      setLockedFile(file);
                      setShowUpgradeModal(true);
                      return;
                    }
                    setSelectedFile(file);
                    setCurrentHierarchy({ technology: tech, topic: topic, subtopic: subtopic });
                    if (window.innerWidth < 1024) setIsSidebarOpen(false);
                  }}
                  theme={theme}
                  selectedFile={selectedFile}
                  isPaid={hasPremiumAccess}
                  isPurple={!isDark}
                />
              </div>
            </aside>

            {/* Main Content Area */}
            <div 
              className={`flex-1 overflow-y-auto relative scroll-smooth flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? "ml-80" : "ml-0"}`} 
              id="mastery-content-area"
            >
              {/* Sticky Hierarchy Header */}
              {selectedFile && currentHierarchy.technology && (
                <div className={`sticky top-0 z-[60] px-6 py-3 border-b backdrop-blur-md flex items-center gap-2 text-sm font-medium ${
                  isDark ? "bg-[#0B0F1A]/90 border-white/5 text-gray-400" : "bg-white/90 border-gray-100 text-gray-600"
                }`}>
                  <span className="capitalize">{currentHierarchy.technology.replace(/-/g, ' ')}</span>
                  <ChevronRight size={14} className="opacity-50" />
                  <span className="text-primary font-bold">{currentHierarchy.topic}</span>
                  {currentHierarchy.subtopic && (
                    <>
                      <ChevronRight size={14} className="opacity-50" />
                      <span className={isDark ? "text-white" : "text-slate-900"}>{currentHierarchy.subtopic}</span>
                    </>
                  )}
                </div>
              )}

              {/* Sticky Progress Bar */}
              <div className="sticky top-[45px] left-0 right-0 z-40 h-1 bg-transparent">
                <div 
                  className="h-full bg-gradient-to-r from-[#5B21B6] to-[#7C3AED] transition-all duration-150"
                  style={{ width: `${readingProgress}%` }}
                />
              </div>

              {!selectedFile ? (
                <div className={`flex-1 flex items-center justify-center p-8 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  <div className="text-center space-y-6 max-w-md">
                    <div className={`mx-auto w-24 h-24 rounded-3xl flex items-center justify-center ${isDark ? "bg-white/5 shadow-2xl shadow-purple-500/10" : "bg-primary/5 shadow-2xl shadow-primary/10"}`}>
                      <Sparkles size={48} className="text-[#5B21B6] animate-pulse" />
                    </div>
                    <div>
                      <h3 className={`text-2xl font-black ${isDark ? "text-white" : "text-gray-900"}`}>
                        Select a topic to start learning
                      </h3>
                      <p className="text-base mt-3 leading-relaxed">
                        Embark on your journey to master {hasSelection?.replace('-premium', '') || 'Elite Technologies'} with our premium, high-fidelity documentation.
                      </p>
                    </div>
                    <button 
                       onClick={() => setIsSidebarOpen(true)}
                       className="lg:hidden px-6 py-3 bg-[#5B21B6] text-white rounded-xl font-bold text-sm shadow-xl shadow-purple-500/20"
                    >
                      Open Library
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex relative min-w-0">
                  {/* Center Documentation Content */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="max-w-[900px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-10">
                      {/* Breadcrumbs & Navigation */}
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center border border-purple-200 shadow-sm">
                             <Sparkles className="w-5 h-5 text-[#5B21B6]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400">
                               <span>Elite Mastery</span>
                               <ChevronRight size={10} />
                               <span className="text-[#5B21B6]">{currentHierarchy.technology || hasSelection?.replace('-premium', '')}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                               <span className={`text-xs font-bold ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                                 {currentHierarchy.topic || flatSubtopics[currentIndex]?.topicName}
                               </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 bg-white/50 dark:bg-white/5 backdrop-blur-md p-1.5 rounded-2xl border border-purple-100 dark:border-white/10 shadow-sm self-end sm:self-auto">
                            <button 
                                onClick={handlePrev}
                                disabled={currentIndex <= 0}
                                className="p-2.5 rounded-xl hover:bg-purple-100 dark:hover:bg-white/10 text-[#5B21B6] transition-all disabled:opacity-30"
                                title="Previous Topic"
                            >
                                <ArrowLeft size={18} />
                            </button>
                            <div className="h-6 w-px bg-purple-100 dark:bg-white/10 mx-1"></div>
                            <button 
                                onClick={handleNext}
                                disabled={currentIndex >= flatSubtopics.length - 1}
                                className="p-2.5 rounded-xl hover:bg-purple-100 dark:hover:bg-white/10 text-[#5B21B6] transition-all disabled:opacity-30"
                                title="Next Topic"
                            >
                                <ArrowRight size={18} />
                            </button>
                        </div>
                      </div>

                      {/* Content Info Bar */}
                      <div className={`mb-12 flex flex-wrap gap-6 p-5 rounded-2xl border ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-100 shadow-sm"}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <Clock size={16} className="text-emerald-500" />
                          </div>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Reading Time</p>
                            <p className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{readingTime} mins</p>
                          </div>
                        </div>
                        <div className="h-10 w-px bg-gray-200 dark:bg-white/10 hidden sm:block"></div>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                            <Target size={16} className="text-blue-500" />
                          </div>
                          <div>
                            <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Progress</p>
                            <p className={`text-xs font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{Math.round(readingProgress)}%</p>
                          </div>
                        </div>
                      </div>

                      <article className="min-w-0">
                        {/* Professional Documentation Explorer Hierarchy */}
                        {!selectedFile ? (
                          <div className="p-8 max-w-5xl mx-auto w-full">
                            {!selectedTopic ? (
                              <div className="space-y-6">
                                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>Select a Topic</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {filteredTechnologies[0]?.topics?.map((t, idx) => (
                                    <div 
                                      key={idx}
                                      onClick={() => {
                                        setSelectedTopic(t);
                                        setViewMode('topic-detail');
                                      }}
                                      className={`p-6 rounded-2xl border cursor-pointer transition-all hover:shadow-lg ${
                                        isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-slate-200 hover:border-primary/50"
                                      }`}
                                    >
                                      <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{t.topic}</h3>
                                      <p className="text-sm text-gray-500 mt-2">{t.subtopics?.length || 0} Subtopics</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : !selectedSubtopic ? (
                              <div className="space-y-6">
                                <div className="flex items-center gap-2 text-sm text-primary font-bold cursor-pointer" onClick={() => setSelectedTopic(null)}>
                                  <ArrowLeft size={16} /> Back to Topics
                                </div>
                                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{selectedTopic.topic}</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {selectedTopic.subtopics?.map((s, idx) => (
                                    <div 
                                      key={idx}
                                      onClick={() => {
                                        setSelectedSubtopic(s);
                                        setViewMode('subtopic-detail');
                                      }}
                                      className={`p-6 rounded-2xl border cursor-pointer transition-all hover:shadow-lg ${
                                        isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-slate-200 hover:border-primary/50"
                                      }`}
                                    >
                                      <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{s.subtopic}</h3>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-6">
                                <div className="flex items-center gap-2 text-sm text-primary font-bold cursor-pointer" onClick={() => setSelectedSubtopic(null)}>
                                  <ArrowLeft size={16} /> Back to Subtopics
                                </div>
                                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{selectedSubtopic.subtopic}</h2>
                                <div className="space-y-3">
                                  {/* In this structure, subtopic might directly contain files or we treat it as files */}
                                  {/* If subtopic has a .file property, we show it, otherwise we might need to adjust based on actual data structure */}
                                  <div 
                                    onClick={() => {
                                      setSelectedFile(selectedSubtopic.file);
                                      setCurrentHierarchy({
                                        technology: filteredTechnologies[0].technology,
                                        topic: selectedTopic.topic,
                                        subtopic: selectedSubtopic.subtopic
                                      });
                                    }}
                                    className={`p-4 rounded-xl border cursor-pointer flex items-center gap-3 ${
                                      isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-slate-200 hover:border-primary"
                                    }`}
                                  >
                                    <BookOpen size={18} className="text-primary" />
                                    <span className={isDark ? "text-white" : "text-slate-900"}>Read Documentation</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <EliteMarkdownViewer
                            filePath={selectedFile} 
                            onHeadingsCollected={setHeadings}
                            onContentLengthUpdate={(len) => setReadingTime(Math.ceil(len / 1000) + 1)}
                            readingProgress={readingProgress}
                            setReadingProgress={setReadingProgress}
                            activeHeading={activeHeading}
                            setActiveHeading={setActiveHeading}
                            headings={headings}
                          />
                        )}
                      </article>

                      {/* Bottom Navigation */}
                      <div className="mt-20 pt-10 border-t border-gray-100 dark:border-white/10 flex items-center justify-between gap-4">
                        {currentIndex > 0 ? (
                          <button 
                            onClick={handlePrev}
                            className={`flex-1 max-w-[240px] flex items-center gap-4 p-4 rounded-2xl border text-left transition-all ${
                              isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-gray-100 hover:border-purple-200 hover:shadow-lg"
                            }`}
                          >
                            <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center">
                              <ArrowLeft size={20} className="text-gray-400" />
                            </div>
                            <div className="overflow-hidden">
                              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Previous</p>
                              <p className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                                {flatSubtopics[currentIndex - 1].title}
                              </p>
                            </div>
                          </button>
                        ) : <div className="flex-1" />}

                        {currentIndex < flatSubtopics.length - 1 ? (
                          <button 
                            onClick={handleNext}
                            className={`flex-1 max-w-[240px] flex items-center justify-between p-4 rounded-2xl border text-right transition-all ${
                              isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-gray-100 hover:border-purple-200 hover:shadow-lg"
                            }`}
                          >
                            <div className="overflow-hidden text-right">
                              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Next</p>
                              <p className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-gray-900"}`}>
                                {flatSubtopics[currentIndex + 1].title}
                              </p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-[#5B21B6]/10 flex items-center justify-center">
                              <ArrowRight size={20} className="text-[#5B21B6]" />
                            </div>
                          </button>
                        ) : <div className="flex-1" />}
                      </div>
                    </div>
                  </div>

                  {/* Right Sidebar - Table of Contents */}
                  <aside className="hidden xl:block w-72 h-full flex-shrink-0 border-l border-gray-100 dark:border-white/5 sticky top-0 p-8 overflow-y-auto custom-scrollbar">
                    <div className="flex items-center gap-3 mb-8">
                       <List size={18} className="text-[#5B21B6]" />
                       <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-[#5B21B6]">On this page</h4>
                    </div>
                    <nav className="space-y-1">
                      {headings.length > 0 ? headings.map((heading) => (
                        <button
                          key={heading.id}
                          onClick={() => scrollToHeading(heading.id)}
                          className={`
                            block w-full text-left py-2 px-3 rounded-xl text-xs transition-all duration-300
                            ${heading.level === 3 ? 'pl-8' : 'font-bold'}
                            ${activeHeading === heading.id 
                              ? 'text-[#5B21B6] bg-[#5B21B6]/5 border-l-2 border-[#5B21B6] rounded-l-none' 
                              : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-[#5B21B6] hover:bg-purple-50'}
                          `}
                        >
                          {heading.text}
                        </button>
                      )) : (
                        <p className="text-xs text-gray-400 italic">No sections found</p>
                      )}
                    </nav>

                    <div className="mt-12 p-6 rounded-3xl bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] shadow-xl shadow-purple-500/20 text-white relative overflow-hidden">
                       <div className="relative z-10">
                         <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-80">Current Topic</p>
                         <h5 className="text-sm font-black mt-2 leading-tight">
                           {flatSubtopics[currentIndex]?.title}
                         </h5>
                         <div className="mt-4 h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
                            <div className="h-full bg-white" style={{ width: `${readingProgress}%` }} />
                         </div>
                         <p className="text-[10px] font-bold mt-2 opacity-80">
                            {Math.round(readingProgress)}% Completed
                         </p>
                       </div>
                       <Sparkles className="absolute -bottom-4 -right-4 w-20 h-20 opacity-20" />
                    </div>
                  </aside>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* 🧩 Modals */}
      
      {/* Floating Actions Integration */}
      <FloatingActions 
        content={selectedFile ? "Learning elite technology content..." : ""} 
        onChatToggle={() => setShowChat(!showChat)}
      />

      {/* Mode Selection Modal */}
      {/* Selection Modal */}
      <div className="relative z-[2000]">
        <EliteSelectionModal
          isOpen={showSelectionModal}
          onClose={() => {
            setShowSelectionModal(false);
          }}
          userId={getUserId()}
          onSelectionSuccess={handleSelectionSuccess}
        />
      </div>

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Unlock All Elite Technologies"
        message="Upgrade to premium to access all Elite Technology documentation without limits. View both Java Premium and Python Premium with full content access."
      />

      {/* ChatBot Integration */}
      {showChat && (
        <div ref={chatBotRef} className="fixed bottom-6 right-6 z-[100]">
          <ChatBot onClose={() => setShowChat(false)} />
        </div>
      )}
    </div>
  );
};

export default EliteTechnologyMastery;
